import { useState, useEffect } from "react";
import { Loader2, Ticket, Plus, Share2, Edit, Trash2, Copy, Check, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =====================================================================
// CREATE / EDIT MODAL
// =====================================================================
const ListingFormModal = ({ open, onClose, onSaved, editing }) => {
  const [form, setForm] = useState({
    event_name: "", event_date: "", event_time: "", event_venue: "", event_city: "",
    section: "", row: "", seats: "", ticket_count: 1, price_per_ticket: "",
    description: "", expires_at: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        event_name: editing.event_name || "",
        event_date: editing.event_date || "",
        event_time: editing.event_time || "",
        event_venue: editing.event_venue || "",
        event_city: editing.event_city || "",
        section: editing.section || "",
        row: editing.row || "",
        seats: editing.seats || "",
        ticket_count: editing.ticket_count || 1,
        price_per_ticket: editing.price_per_ticket || "",
        description: editing.description || "",
        expires_at: editing.expires_at ? editing.expires_at.split("T")[0] : "",
      });
    } else {
      setForm({
        event_name: "", event_date: "", event_time: "", event_venue: "", event_city: "",
        section: "", row: "", seats: "", ticket_count: 1, price_per_ticket: "",
        description: "", expires_at: "",
      });
    }
  }, [editing, open]);

  if (!open) return null;

  const submit = async () => {
    if (!form.event_name || !form.price_per_ticket) {
      toast.error("Nom de l'événement et prix requis");
      return;
    }
    if (!form.event_date) {
      toast.error("La date de l'événement est requise");
      return;
    }
    if (!form.event_time) {
      toast.error("L'heure de l'événement est requise");
      return;
    }
    if (!form.event_venue || form.event_venue.trim().length < 2) {
      toast.error("Le lieu de l'événement est requis (même pour une admission générale)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        ticket_count: parseInt(form.ticket_count) || 1,
        price_per_ticket: parseFloat(form.price_per_ticket),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      if (editing) {
        await axios.put(`${API}/ticket-listings/${editing.id}`, payload);
        toast.success("Annonce mise à jour");
      } else {
        await axios.post(`${API}/ticket-listings`, payload);
        toast.success("Annonce créée — partagez votre lien !");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">
            {editing ? "Modifier l'annonce" : "Créer une annonce de vente"}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full" data-testid="listing-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1.5">Nom de l'événement *</label>
            <input type="text" value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-300 focus:border-brand-300 outline-none"
              placeholder="Ex: Osheaga 2026" data-testid="listing-event-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Date *</label>
              <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" required data-testid="listing-event-date" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Heure *</label>
              <input type="time" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" required data-testid="listing-event-time" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Lieu *</label>
              <input type="text" value={form.event_venue} onChange={(e) => setForm({ ...form, event_venue: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" placeholder="Centre Bell" required data-testid="listing-event-venue" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Ville</label>
              <input type="text" value={form.event_city} onChange={(e) => setForm({ ...form, event_city: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" placeholder="Montréal" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Nb. de billets *</label>
              <input type="number" min="1" max="20" value={form.ticket_count} onChange={(e) => setForm({ ...form, ticket_count: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Section</label>
              <input type="text" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" placeholder="Ex: 220" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Rangée</label>
              <input type="text" value={form.row} onChange={(e) => setForm({ ...form, row: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" placeholder="Ex: B" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Sièges</label>
              <input type="text" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" placeholder="Ex: 12, 13" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">Prix / billet (CAD) *</label>
              <input type="number" step="0.01" min="1" value={form.price_per_ticket} onChange={(e) => setForm({ ...form, price_per_ticket: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" placeholder="150" data-testid="listing-price" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none resize-none"
              placeholder="Détails supplémentaires (billets côte à côte, e-tickets, etc.)" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1.5">Expire le (optionnel)</label>
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg outline-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 border border-gray-300 hover:bg-gray-50 rounded-lg font-medium">Annuler</button>
          <button onClick={submit} disabled={saving} className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-lg font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="listing-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {editing ? "Enregistrer" : "Créer l'annonce"}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// SHARE MODAL
// =====================================================================
const ShareModal = ({ open, onClose, listing }) => {
  const [copied, setCopied] = useState(false);
  if (!open || !listing) return null;

  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const url = `${base.replace(/\/$/, "")}/billet/${listing.slug}`;
  const text = `🎟️ ${listing.ticket_count} billet${listing.ticket_count > 1 ? 's' : ''} pour ${listing.event_name} à vendre sur Middleman`;

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Lien copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const msgShare = `fb-messenger://share/?link=${encodeURIComponent(url)}`;
  const xShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-bold">Partager l'annonce</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
            <input readOnly value={url} className="flex-1 bg-transparent text-sm font-mono text-gray-700 outline-none truncate" />
            <button onClick={copy} className="flex-shrink-0 inline-flex items-center gap-1.5 bg-gray-900 text-white px-3 py-1.5 rounded-md text-sm font-semibold">
              {copied ? <><Check className="h-3.5 w-3.5" /> Copié</> : <><Copy className="h-3.5 w-3.5" /> Copier</>}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <a href={fbShare} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 p-3 bg-[#1877f2] hover:bg-[#1665d0] text-white rounded-lg font-semibold text-xs">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            <a href={msgShare} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 p-3 bg-[#00b2ff] hover:bg-[#0099d8] text-white rounded-lg font-semibold text-xs">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.936 1.444 5.544 3.746 7.256v3.501l3.373-1.851c.9.25 1.851.384 2.881.384 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.093 12.441l-2.545-2.72-4.968 2.72 5.467-5.803 2.609 2.72 4.905-2.72-5.468 5.803z"/></svg>
              Messenger
            </a>
            <a href={xShare} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 p-3 bg-black hover:bg-gray-900 text-white rounded-lg font-semibold text-xs">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X / Twitter
            </a>
          </div>
          <a href={`https://www.instagram.com/`} target="_blank" rel="noopener noreferrer" className="block text-center p-3 bg-gradient-to-r from-[#e1306c] to-[#fd1d1d] hover:opacity-90 text-white rounded-lg font-semibold text-sm" onClick={copy}>
            📷 Copier le lien + ouvrir Instagram
          </a>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// MAIN MANAGER
// =====================================================================
const TicketListingsManager = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);

  const fetchMine = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ticket-listings/mine`);
      setListings(res.data.listings || []);
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMine(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await axios.delete(`${API}/ticket-listings/${id}`);
      toast.success("Annonce supprimée");
      fetchMine();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleCancel = async (id) => {
    try {
      await axios.put(`${API}/ticket-listings/${id}`, { status: "cancelled" });
      toast.success("Annonce annulée");
      fetchMine();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const statusBadge = (s) => {
    const map = {
      active: { label: "Active", cls: "bg-brand-50 text-brand-700 border-brand-200" },
      sold: { label: "Vendue", cls: "bg-gray-100 text-gray-700 border-gray-300" },
      cancelled: { label: "Annulée", cls: "bg-red-50 text-red-700 border-red-200" },
      expired: { label: "Expirée", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    };
    const c = map[s] || map.active;
    return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${c.cls}`}>{c.label}</span>;
  };

  return (
    <div className="space-y-5" data-testid="ticket-listings-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Ticket className="h-6 w-6 text-brand-700" /> Mes annonces</h2>
          <p className="text-sm text-gray-500 mt-1">Créez une annonce et partagez le lien sur vos réseaux sociaux.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white font-semibold px-5 py-2.5 rounded-lg" data-testid="create-listing-btn">
          <Plus className="h-4 w-4" /> Créer une annonce
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-7 w-7 animate-spin text-brand-700 mx-auto" /></div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
          <Ticket className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-1 font-medium">Aucune annonce pour l'instant</p>
          <p className="text-sm text-gray-400 mb-5">Créez votre première annonce et partagez-la sur vos réseaux !</p>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white px-5 py-2.5 rounded-lg font-semibold">
            <Plus className="h-4 w-4" /> Créer ma première annonce
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((l) => (
            <div key={l.id} className="bg-white border border-gray-200 hover:border-brand-300 hover:shadow-md transition-all rounded-xl p-5">
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="font-bold text-gray-900 leading-tight">{l.event_name}</h3>
                {statusBadge(l.status)}
              </div>
              <div className="space-y-1 text-sm text-gray-600 mb-4">
                {l.event_date && <p>📅 {l.event_date}{l.event_time ? ` • ${l.event_time}` : ''}</p>}
                {l.event_venue && <p>📍 {l.event_venue}{l.event_city ? `, ${l.event_city}` : ''}</p>}
                {(l.section || l.row || l.seats) && (
                  <p className="text-xs">
                    {l.section && `Sect. ${l.section}`}
                    {l.row && ` · Rangée ${l.row}`}
                    {l.seats && ` · Siège(s) ${l.seats}`}
                  </p>
                )}
              </div>
              <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-gray-100">
                <div>
                  <p className="text-xs text-gray-500">{l.ticket_count} billet{l.ticket_count > 1 ? 's' : ''}</p>
                  <p className="text-xl font-bold text-gray-900">{l.price_per_ticket} {l.currency}<span className="text-xs font-normal text-gray-500"> / billet</span></p>
                </div>
                <p className="text-xs text-gray-400">{l.views || 0} vue{(l.views || 0) > 1 ? 's' : ''}</p>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {l.status === 'active' && (
                  <button onClick={() => setShareTarget(l)} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-300 hover:bg-brand-400 text-gray-900 font-semibold text-xs px-3 py-2 rounded-md" data-testid={`share-listing-${l.id}`}>
                    <Share2 className="h-3.5 w-3.5" /> Partager
                  </button>
                )}
                <a href={`/billet/${l.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs px-3 py-2 rounded-md font-medium">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                {l.status !== 'sold' && (
                  <>
                    <button onClick={() => { setEditing(l); setShowForm(true); }} className="inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md" title="Modifier">
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    {l.status === 'active' && (
                      <button onClick={() => handleCancel(l.id)} className="inline-flex items-center justify-center bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-3 py-2 rounded-md" title="Annuler">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => handleDelete(l.id)} className="inline-flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-md" title="Supprimer">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ListingFormModal open={showForm} onClose={() => setShowForm(false)} onSaved={fetchMine} editing={editing} />
      <ShareModal open={!!shareTarget} onClose={() => setShareTarget(null)} listing={shareTarget} />
    </div>
  );
};

export default TicketListingsManager;
